import React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Container,
  CardActionArea,
  Button,
  Divider,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Paper
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import SearchIcon from '@mui/icons-material/Search';
import HomeIcon from '@mui/icons-material/Home';
import FavoriteIcon from '@mui/icons-material/Favorite';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate } from 'react-router-dom';
import { 
  useAppointmentsListQuery
} from '../../../services/GLOWClient';
import { 
  useFavoritesListQuery,
  useToggleFavoriteMutation
} from '../../../services/salonsApi';
import { theme } from '../../../theme';
import { TitleScreen } from '../../../SystemDesign/styles';
import { PrimaryButton } from '../../../SystemDesign/styles';
import { BASE_URL } from '../../../config';
import FutureAppointmentDropdown from '../header/futureAppointment';
import ClientSalonList from './ClientSalonList';
import { getLocalizedField } from '../../../utils/localizedFields';

// Import the AppointmentsList component from clientView.jsx
import { AppointmentsList } from '../clientView';

// Helper function to build image URL - identical to the one in service.jsx
const buildImageUrl = (url) => {
  if (!url) return '';
  
  // If URL is already absolute, return as is
  if (/^https?:\/\//i.test(url)) {
    // Check if it's a localhost URL without port and add port 8012
    if (url.match(/^http:\/\/localhost\//) && !url.match(/^http:\/\/localhost:[0-9]+\//)) {
      return url.replace('http://localhost/', 'http://localhost:8012/');
    }
    return url;
  }
  
  // For relative URLs that start with /media/
  if (url.startsWith('/media/')) {
    // Use port 8012 for localhost in development
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    return `${baseUrl}${url}`;
  }
  
  // For relative URLs that start with media/ (no leading slash)
  if (url.startsWith('media/')) {
    // Use port 8012 for localhost in development
    const baseUrl = window.location.hostname === 'localhost' ? 
      'http://localhost:8012' : 
      BASE_URL.replace(/\/$/, ''); // Remove trailing slash
    return `${baseUrl}/${url}`;
  }
  
  // For other relative URLs, prepend BASE_URL
  const baseUrl = window.location.hostname === 'localhost' ? 
    'http://localhost:8012' : 
    BASE_URL.replace(/\/$/, ''); // Remove trailing slash
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${baseUrl}${cleanUrl}`;
};

const ClientDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  
  // Tab state - Default to "Mes Rendez-vous" tab (1)
  const [tabValue, setTabValue] = useState(1);
  
  // Modal state for adding favorites
  const [showSalonListModal, setShowSalonListModal] = useState(false);
  
  // Delete confirmation dialog state
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [salonToDelete, setSalonToDelete] = useState(null);
  
  // Query favorites
  const { data: favorites, error: favoritesError, isLoading: favoritesLoading, refetch: refetchFavorites } = useFavoritesListQuery({ language: i18n.language });
  const [toggleFavorite] = useToggleFavoriteMutation();
  
  // Query appointments
  const { data: appointments, error: appointmentsError, isLoading: appointmentsLoading } = useAppointmentsListQuery();

  // State to track current photo index for each salon
  const [currentPhotoIndices, setCurrentPhotoIndices] = useState({});

  const handleSalonClick = (salonId) => {
    navigate(`/salon/${salonId}`);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSearchSalonRedirect = () => {
    navigate('/search-salon');
  };

  const handleOpenSalonListModal = () => {
    setShowSalonListModal(true);
  };

  const handleCloseSalonListModal = () => {
    setShowSalonListModal(false);
  };

  const handleRemoveFromFavorites = (salon, event) => {
    event.stopPropagation(); // Prevent card click
    setSalonToDelete(salon);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteFavorite = async () => {
    if (!salonToDelete) return;
    
    try {
      await toggleFavorite(salonToDelete.salon_id).unwrap();
      await refetchFavorites(); // Refetch the favorites list
      setDeleteConfirmOpen(false);
      setSalonToDelete(null);
    } catch (error) {
      console.error('Error removing from favorites:', error);
    }
  };

  const cancelDeleteFavorite = () => {
    setDeleteConfirmOpen(false);
    setSalonToDelete(null);
  };
  
  // Function to handle previous photo navigation
  const handlePrevPhoto = (e, salonId) => {
    e.stopPropagation(); // Prevent card click
    setCurrentPhotoIndices(prev => {
      const salon = favorites?.find(s => s.salon_id === salonId);
      const photoCount = getSalonPhotoCount(salon);
      if (photoCount <= 1) return prev;
      
      const currentIndex = prev[salonId] || 0;
      return {
        ...prev,
        [salonId]: (currentIndex - 1 + photoCount) % photoCount
      };
    });
  };
  
  // Function to handle next photo navigation
  const handleNextPhoto = (e, salonId) => {
    e.stopPropagation(); // Prevent card click
    setCurrentPhotoIndices(prev => {
      const salon = favorites?.find(s => s.salon_id === salonId);
      const photoCount = getSalonPhotoCount(salon);
      if (photoCount <= 1) return prev;
      
      const currentIndex = prev[salonId] || 0;
      return {
        ...prev,
        [salonId]: (currentIndex + 1) % photoCount
      };
    });
  };
  
  // Helper function to get photo count for a salon
  const getSalonPhotoCount = (salon) => {
    if (!salon) return 0;
    
    if (salon.photos && Array.isArray(salon.photos) && salon.photos.length > 0) {
      return salon.photos.length;
    }
    
    if (salon.photo) {
      return 1;
    }
    
    return 0;
  };
  
  // Helper to find primary photo index
  const getPrimaryPhotoIndex = (salon) => {
    if (!salon || !Array.isArray(salon.photos)) return 0;
    const idx = salon.photos.findIndex(p => typeof p === 'object' && p && p.is_primary);
    return idx >= 0 ? idx : 0;
  };

  // Helper function to get current photo URL for a salon
  const getSalonPhotoUrl = (salon, salonId) => {
    if (!salon) return null;
    
    // Prefer the primary photo when no index is set yet
    const currentIndex = (salonId in currentPhotoIndices)
      ? currentPhotoIndices[salonId]
      : getPrimaryPhotoIndex(salon);
    
    // Case 1: Check photos array
    if (salon.photos && Array.isArray(salon.photos) && salon.photos.length > 0) {
      const photo = salon.photos[currentIndex];
      
      if (typeof photo === 'string') {
        return buildImageUrl(photo);
      } else if (photo && photo.url) {
        return buildImageUrl(photo.url);
      }
    }
    
    // Case 2: Check if there's a direct photo property as fallback
    if (salon.photo) {
      return buildImageUrl(salon.photo);
    }
    
    // Default placeholder
    return 'https://via.placeholder.com/400x240?text=Salon+Image';
  };
  
  // Initialize photo indices when favorites data is loaded, preferring primary
  useEffect(() => {
    if (favorites && favorites.length > 0) {
      const initialIndices = {};
      favorites.forEach(favorite => {
        const salon = favorite.salon || favorite;
        const salonId = favorite.salon_id || salon.salon_id || salon.id;
        initialIndices[salonId] = getPrimaryPhotoIndex(salon);
      });
      setCurrentPhotoIndices(initialIndices);
    }
  }, [favorites]);

  if (favoritesLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: theme.colors.primary[500] }} />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with Gradient */}
      <Paper sx={{ mb: 4, overflow: 'hidden' }}>
        <Box sx={{ 
          background: `linear-gradient(135deg, ${theme.colors.primary[200]} 0%, ${theme.colors.secondary[100]} 100%)`,
          p: 4,
          color: 'white',
          textAlign: 'center'
        }}>
          {/* Title */}
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 800,
              mb: 2,
              fontSize: { xs: '1.5rem', sm: '2.5rem', md: '3rem' },
              lineHeight: 1.2,
              mb: 1,
              color: theme.colors.primary[600]
            }}
          >
            {t('client_dashboard.my_space')}
          </Typography>
          
          {/* Subtitle */}
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight:  { xs: '150', sm: '200', md: '400' },
              opacity: 0.95,
              mb: 4,
               color: theme.colors.primary[900]

            }}
          >
            {t('client_dashboard.manage_favorites')}
          </Typography>
          
          {/* Search Salon Button */}
          <Button
            variant="contained"
            onClick={() => navigate('/search-salon')}
            startIcon={<SearchIcon />}
            sx={{
              backgroundColor: 'rgba(144, 81, 9, 0.2)',
              backdropFilter: 'blur(10px)',
              color:theme.colors.primary[700] ,
              fontSize: '1rem',
              fontWeight: 600,
              padding: '12px 32px',
              borderRadius: '30px',
              textTransform: 'none',
              border: '2px solid rgba(132, 70, 8, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.3)',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.2)'
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          >
            {t('client_dashboard.search_salon')}
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          centered
          sx={{
            '& .MuiTab-root': {
              fontSize: { xs: '0.7rem', sm: '0.9rem', md: '1rem' },
              fontWeight: 600,
              textTransform: 'none',
              minWidth: { xs: 'auto', sm: 160, md: 200 },
              px: { xs: 1, sm: 2 },
              py: { xs: 1.5, sm: 2 },
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }
          }}
        >
          <Tab 
            icon={<FavoriteIcon />} 
            label={t('client_dashboard.my_favorites_tab')} 
            iconPosition="start"
            sx={{ gap: 1 }}
          />
          <Tab 
            icon={<EventIcon />} 
            label={t('client_dashboard.my_appointments_tab')} 
            iconPosition="start"
            sx={{ gap: 1 }}
          />
        </Tabs>
      </Box>
      
      {/* Tab Content */}
      {tabValue === 0 && (
        <Box>
          {/* Favorites Tab */}
          <Box sx={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            mt: 2 
          }}>
            {/* Add favorites button */}
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'flex-end', 
              width: '100%', 
              maxWidth: '900px',
              mb: 2 
            }}>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleSearchSalonRedirect}
                sx={{
                  ...PrimaryButton,
                  backgroundColor: theme.colors.primary[500],
                  color: '#ffffff',
                  fontSize: '1rem',
                  fontWeight: 600,
                  padding: '12px 24px',
                  borderRadius: '30px',
                  textTransform: 'none'
                }}
              >
                {t('client_dashboard.add_favorite')}
              </Button>
            </Box>

            {/* Empty state for favorites */}
            {(!favorites || favorites.length === 0) && (
              <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 8 }}>
                <FavoriteIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('client_dashboard.no_favorite_salons')}
                </Typography>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  {t('client_dashboard.add_favorite_description')}
                </Typography>
              </Box>
            )}
            
            {/* Favorites list */}
            {favorites && favorites.map((favorite) => {
              const salon = favorite.salon || favorite;
              const salonId = favorite.salon_id || salon.salon_id || salon.id;
              const salonName = getLocalizedField(salon, 'name', i18n.language) || 'Salon';
              return (
                <Card
                  key={salonId}
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%',
                    maxWidth: '900px',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    borderRadius: '16px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    border: `1px solid ${theme.colors.secondary[100]}`,
                    backgroundColor: '#ffffff',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.15)',
                      borderColor: theme.colors.primary[200],
                      '& .delete-button': {
                        opacity: 1,
                        visibility: 'visible'
                      }
                    },
                    cursor: 'pointer',
                  }}
                  onClick={() => handleSalonClick(salonId)}
                >
                  {/* Delete button - appears on hover */}
                  <Tooltip title={t('client_dashboard.remove_from_favorites')} placement="top">
                    <IconButton
                      className="delete-button"
                      onClick={(e) => handleRemoveFromFavorites(salon, e)}
                      sx={{
                        position: 'absolute',
                        top: 12,
                        right: 12,
                        backgroundColor: 'rgba(244, 67, 54, 0.9)',
                        color: 'white',
                        opacity: 0,
                        visibility: 'hidden',
                        transition: 'all 0.3s ease',
                        zIndex: 2,
                        '&:hover': {
                          backgroundColor: 'rgba(244, 67, 54, 1)',
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>

                  <Box sx={{
                    position: 'relative',
                    width: { xs: '100%', md: '40%' },
                    height: { xs: 220, md: 240 },
                  }}>
                    <CardMedia
                      component="img"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      image={getSalonPhotoUrl(salon, salonId)}
                      alt={salonName}
                    />
                    
                    {/* Photo navigation arrows - only show if multiple photos */}
                    {getSalonPhotoCount(salon) > 1 && (
                      <>
                        <IconButton 
                          onClick={(e) => handlePrevPhoto(e, salonId)}
                          sx={{
                            position: 'absolute',
                            left: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.7)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            width: 30,
                            height: 30,
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        >
                          <ArrowBackIosNewIcon />
                        </IconButton>
                        
                        <IconButton 
                          onClick={(e) => handleNextPhoto(e, salonId)}
                          sx={{
                            position: 'absolute',
                            right: 8,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            bgcolor: 'rgba(255,255,255,0.7)',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                            width: 30,
                            height: 30,
                            '& .MuiSvgIcon-root': { fontSize: 18 }
                          }}
                        >
                          <ArrowForwardIosIcon />
                        </IconButton>
                        
                        {/* Photo counter */}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 8,
                            right: 8,
                            bgcolor: 'rgba(0,0,0,0.5)',
                            color: 'white',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem'
                          }}
                        >
                          {(currentPhotoIndices[salonId] || 0) + 1}/{getSalonPhotoCount(salon)}
                        </Box>
                      </>
                    )}
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    flex: 1,
                    p: { xs: 3, md: 4 },
                    justifyContent: 'space-between'
                  }}>
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography 
                          variant="h4" 
                          component="h2" 
                          sx={{ 
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '1.75rem' },
                            lineHeight: 1.2,
                            mb: 1,
                            color: theme.colors.primary[700]
                          }}
                        >
                          {salonName}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'text.secondary',
                            fontWeight: 500,
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: '0.95rem',
                            mb: 0.5
                          }}
                        >
                          <Box component="span" sx={{ mr: 1, display: 'flex', alignItems: 'center' }}>
                            📍
                          </Box>
                          {salon.address}
                        </Typography>
                      </Box>
                          
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        {salon.description && (
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              mb: 3,
                              lineHeight: 1.7,
                              fontSize: '0.95rem',
                              color: 'text.secondary'
                            }}
                          >
                            {salon.description.length > 150
                              ? `${salon.description.substring(0, 150)}...`
                              : salon.description}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'flex-end',
                      mt: 'auto'
                    }}>
                      <Button
                        variant="contained"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSalonClick(salonId);
                        }}
                        sx={{
                          ...PrimaryButton,
                          backgroundColor: theme.colors.primary[500],
                          color: '#ffffff',
                          fontSize: '1rem',
                          fontWeight: 600,
                          padding: '12px 28px',
                          borderRadius: '30px',
                          textTransform: 'none',
                          minWidth: '140px',
                          boxShadow: '0 4px 16px rgba(226, 194, 156, 0.25)',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            backgroundColor: theme.colors.primary[400],
                            transform: 'translateY(-2px)',
                            boxShadow: '0 8px 24px rgba(226, 194, 156, 0.35)'
                          }
                        }}
                      >
                        {t('client_dashboard.view_details')}
                      </Button>
                    </Box>
                  </Box>
                </Card>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Appointments Tab */}
      {tabValue === 1 && (
        <Box sx={{ mt: 2 }}>
          <AppointmentsList />
        </Box>
      )}

      {/* Modal for adding favorites */}
      <Dialog
        open={showSalonListModal}
        onClose={handleCloseSalonListModal}
        maxWidth="xl"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          fontSize: '1.5rem',
          fontWeight: 700,
          color: theme.colors.primary[700],
          pb: 1
        }}>
          {t('client_dashboard.select_salon_to_add')}
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <ClientSalonList />
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center' }}>
          <Button
            onClick={handleCloseSalonListModal}
            variant="outlined"
            sx={{
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4
            }}
          >
            {t('client_dashboard.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={cancelDeleteFavorite}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            borderRadius: '16px'
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center',
          fontSize: '1.25rem',
          fontWeight: 600,
          color: 'text.primary',
          pb: 1
        }}>
          {t('client_dashboard.confirm_delete')}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 3 }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('client_dashboard.confirm_delete_message')}
          </Typography>
          {salonToDelete && (
            <Typography variant="h6" sx={{ 
              fontWeight: 600,
              color: theme.colors.primary[700]
            }}>
              {salonToDelete.name}
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, justifyContent: 'center', gap: 2 }}>
          <Button
            onClick={cancelDeleteFavorite}
            variant="outlined"
            sx={{
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              borderColor: 'divider',
              color: 'text.secondary'
            }}
          >
            {t('client_dashboard.cancel')}
          </Button>
          <Button
            onClick={confirmDeleteFavorite}
            variant="contained"
            sx={{
              borderRadius: '25px',
              textTransform: 'none',
              fontWeight: 600,
              px: 4,
              backgroundColor: '#f44336',
              '&:hover': {
                backgroundColor: '#d32f2f'
              }
            }}
          >
            {t('client_dashboard.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ClientDashboard;
